"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import { XMark } from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import LanguageSwitcher from "@modules/layout/components/language-switcher"
import { Fragment } from "react"
import { useT } from "@lib/i18n/use-t"
import type { UiKey } from "@lib/i18n/ui"

const items: { key: UiKey; href: string }[] = [
  { key: "home", href: "/" },
  { key: "catalog", href: "/store" },
  { key: "sellers", href: "/sellers" },
  { key: "cabinet", href: "/account" },
  { key: "cart", href: "/cart" },
]

const secondaryItems: { key: UiKey; href: string }[] = [
  { key: "help", href: "/info/help" },
  { key: "deliveryPay", href: "/info/delivery" },
]

export default function SideMenu() {
  const t = useT()
  return (
    <Popover className="relative h-full">
      {({ open, close }) => (
        <>
          <Popover.Button className="flex h-full items-center text-sm font-medium text-slate-700 transition hover:text-slate-950 dark:text-slate-300 dark:hover:text-white">
            {t("menu")}
          </Popover.Button>

          {open && (
            <div
              className="fixed inset-0 z-[80] bg-slate-950/45 backdrop-blur-sm"
              onClick={close}
            />
          )}

          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="-translate-x-full opacity-0"
            enterTo="translate-x-0 opacity-100"
            leave="transition ease-in duration-150"
            leaveFrom="translate-x-0 opacity-100"
            leaveTo="-translate-x-full opacity-0"
          >
            <PopoverPanel className="fixed left-0 top-0 z-[90] flex h-screen w-[86vw] max-w-[380px] flex-col border-r border-slate-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#050816]">
              <div className="mb-8 flex items-center justify-between">
                <LocalizedClientLink
                  href="/"
                  onClick={close}
                  className="flex items-center gap-3"
                >
                  <img src="/logo-mark.svg" alt="KVATRONIX" className="h-10 w-10" />
                  <span className="font-semibold tracking-[0.18em] text-slate-950 dark:text-white">
                    KVATRONIX
                  </span>
                </LocalizedClientLink>

                <button
                  onClick={close}
                  className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10"
                  aria-label={t("closeMenu")}
                >
                  <XMark />
                </button>
              </div>

              <nav className="space-y-2">
                {items.map((item) => (
                  <LocalizedClientLink
                    key={item.key}
                    href={item.href}
                    onClick={close}
                    className="block rounded-2xl px-4 py-3 text-lg font-medium text-slate-700 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
                  >
                    {t(item.key)}
                  </LocalizedClientLink>
                ))}
              </nav>

              <div className="mt-6 border-t border-slate-200 pt-4 dark:border-white/10">
                <div className="mb-3 px-4">
                  <LanguageSwitcher />
                </div>
                <nav className="space-y-1">
                  {secondaryItems.map((item) => (
                    <LocalizedClientLink
                      key={item.key}
                      href={item.href}
                      onClick={close}
                      className="block rounded-xl px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white"
                    >
                      {t(item.key)}
                    </LocalizedClientLink>
                  ))}
                </nav>
              </div>

              <div className="mt-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                <div className="font-medium text-slate-950 dark:text-white">
                  {t("sideMenuHeading")}
                </div>
                <div className="mt-1">
                  {t("sideMenuSub")}
                </div>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  )
}
